import { computed, effect, Injectable, signal } from '@angular/core';

export interface GlobalEventData<T = Event> {
  event: T;
  timestamp: number;
  target: EventTarget | null;
}

export interface KeyboardEventData extends GlobalEventData<KeyboardEvent> {
  key: string;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  metaKey: boolean;
}

export interface MouseEventData extends GlobalEventData<MouseEvent> {
  button: number;
  clientX: number;
  clientY: number;
}

export type SyncEventCallback<T> = (data: T) => boolean | void;

// Document-level capture-phase listeners bypass PrimeNG's focus-stealing
// (p-tabs auto-focuses on switch, killing @HostListener-bound shortcuts).
@Injectable({
  providedIn: 'root',
})
export class GlobalEventsService {
  private readonly _lastKeydown = signal<KeyboardEventData | null>(null);
  private readonly _lastKeyup = signal<KeyboardEventData | null>(null);
  private readonly _lastClick = signal<MouseEventData | null>(null);
  private readonly _lastMouseMove = signal<MouseEventData | null>(null);

  private readonly syncKeydownCallbacks = new Map<string, SyncEventCallback<KeyboardEventData>[]>();
  private readonly syncClickCallbacks: SyncEventCallback<MouseEventData>[] = [];

  readonly lastKeydown = this._lastKeydown.asReadonly();
  readonly lastKeyup = this._lastKeyup.asReadonly();
  readonly lastClick = this._lastClick.asReadonly();
  readonly lastMouseMove = this._lastMouseMove.asReadonly();

  readonly isCtrlA = computed(() => {
    const event = this._lastKeydown();
    return event?.key === 'a' && event?.ctrlKey === true;
  });

  readonly isCtrlC = computed(() => {
    const event = this._lastKeydown();
    return event?.key === 'c' && event?.ctrlKey === true;
  });

  readonly isCtrlV = computed(() => {
    const event = this._lastKeydown();
    return event?.key === 'v' && event?.ctrlKey === true;
  });

  readonly isCtrlX = computed(() => {
    const event = this._lastKeydown();
    return event?.key === 'x' && event?.ctrlKey === true;
  });

  readonly isCtrlS = computed(() => {
    const event = this._lastKeydown();
    return event?.key === 's' && event?.ctrlKey === true;
  });

  readonly isCtrlZ = computed(() => {
    const event = this._lastKeydown();
    return event?.key === 'z' && event?.ctrlKey === true;
  });

  readonly isEscape = computed(() => {
    const event = this._lastKeydown();
    return event?.key === 'escape';
  });

  readonly isEnter = computed(() => {
    const event = this._lastKeydown();
    return event?.key === 'enter';
  });

  private listeners = new Map<string, EventListener>();

  initGlobalListeners(): void {
    this.addListener('keydown', e => this.handleKeydown(e as KeyboardEvent));
    this.addListener('keyup', e => this.handleKeyup(e as KeyboardEvent));
    this.addListener('click', e => this.handleClick(e as MouseEvent));
  }

  private addListener(eventType: string, handler: EventListener): void {
    if (this.listeners.has(eventType)) {
      this.removeListener(eventType);
    }

    this.listeners.set(eventType, handler);
    document.addEventListener(eventType, handler, true);
  }

  private removeListener(eventType: string): void {
    const handler = this.listeners.get(eventType);
    if (handler) {
      document.removeEventListener(eventType, handler, true);
      this.listeners.delete(eventType);
    }
  }

  cleanup(): void {
    this.listeners.forEach((handler, eventType) => {
      document.removeEventListener(eventType, handler, true);
    });
    this.listeners.clear();
    this.syncKeydownCallbacks.clear();
    this.syncClickCallbacks.length = 0;
  }

  private handleKeydown(event: KeyboardEvent): void {
    const data: KeyboardEventData = {
      event,
      timestamp: Date.now(),
      target: event.target,
      key: event.key?.toLowerCase() || '',
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      metaKey: event.metaKey,
    };

    const keyCombo = this.getKeyComboString(data);
    const callbacks = this.syncKeydownCallbacks.get(keyCombo);
    if (callbacks) {
      for (const callback of callbacks) {
        const shouldPreventDefault = callback(data);
        if (shouldPreventDefault === true) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    }

    this._lastKeydown.set(data);
  }

  private handleKeyup(event: KeyboardEvent): void {
    const data: KeyboardEventData = {
      event,
      timestamp: Date.now(),
      target: event.target,
      key: event.key?.toLowerCase() || '',
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      metaKey: event.metaKey,
    };
    this._lastKeyup.set(data);
  }

  private handleClick(event: MouseEvent): void {
    const data: MouseEventData = {
      event,
      timestamp: Date.now(),
      target: event.target,
      button: event.button,
      clientX: event.clientX,
      clientY: event.clientY,
    };

    for (const callback of this.syncClickCallbacks) {
      const shouldPreventDefault = callback(data);
      if (shouldPreventDefault === true) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    this._lastClick.set(data);
  }

  private getKeyComboString(data: KeyboardEventData): string {
    const modifiers = [];
    if (data.ctrlKey) modifiers.push('ctrl');
    if (data.shiftKey) modifiers.push('shift');
    if (data.altKey) modifiers.push('alt');
    if (data.metaKey) modifiers.push('meta');

    return `${modifiers.join('+')}.${data.key}`;
  }

  private getKeyComboStringFromModifiers(
    key: string,
    modifiers: { ctrl?: boolean; shift?: boolean; alt?: boolean; meta?: boolean },
  ): string {
    const mods = [];
    if (modifiers.ctrl) mods.push('ctrl');
    if (modifiers.shift) mods.push('shift');
    if (modifiers.alt) mods.push('alt');
    if (modifiers.meta) mods.push('meta');

    return `${mods.join('+')}.${key.toLowerCase()}`;
  }

  // Returning true from the callback triggers preventDefault + stopPropagation.
  onKeyComboSync(
    key: string,
    modifiers: { ctrl?: boolean; shift?: boolean; alt?: boolean; meta?: boolean } = {},
    callback: SyncEventCallback<KeyboardEventData>,
  ): () => void {
    const comboKey = this.getKeyComboStringFromModifiers(key, modifiers);

    const callbacks = this.syncKeydownCallbacks.get(comboKey) ?? [];
    callbacks.push(callback);
    this.syncKeydownCallbacks.set(comboKey, callbacks);

    return () => {
      const callbacks = this.syncKeydownCallbacks.get(comboKey);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
        if (callbacks.length === 0) {
          this.syncKeydownCallbacks.delete(comboKey);
        }
      }
    };
  }

  onCtrlASync(callback: SyncEventCallback<KeyboardEventData>): () => void {
    return this.onKeyComboSync('a', { ctrl: true }, callback);
  }

  onCtrlCSync(callback: SyncEventCallback<KeyboardEventData>): () => void {
    return this.onKeyComboSync('c', { ctrl: true }, callback);
  }

  onCtrlVSync(callback: SyncEventCallback<KeyboardEventData>): () => void {
    return this.onKeyComboSync('v', { ctrl: true }, callback);
  }

  onCtrlXSync(callback: SyncEventCallback<KeyboardEventData>): () => void {
    return this.onKeyComboSync('x', { ctrl: true }, callback);
  }

  onCtrlSSync(callback: SyncEventCallback<KeyboardEventData>): () => void {
    return this.onKeyComboSync('s', { ctrl: true }, callback);
  }

  onCtrlZSync(callback: SyncEventCallback<KeyboardEventData>): () => void {
    return this.onKeyComboSync('z', { ctrl: true }, callback);
  }

  onEscapeSync(callback: SyncEventCallback<KeyboardEventData>): () => void {
    return this.onKeyComboSync('escape', {}, callback);
  }

  onDeleteSync(callback: SyncEventCallback<KeyboardEventData>): () => void {
    return this.onKeyComboSync('delete', {}, callback);
  }

  onBackspaceSync(callback: SyncEventCallback<KeyboardEventData>): () => void {
    return this.onKeyComboSync('backspace', {}, callback);
  }

  onClickSync(callback: SyncEventCallback<MouseEventData>): () => void {
    this.syncClickCallbacks.push(callback);

    return () => {
      const index = this.syncClickCallbacks.indexOf(callback);
      if (index > -1) {
        this.syncClickCallbacks.splice(index, 1);
      }
    };
  }

  createKeyCombo(key: string, modifiers: { ctrl?: boolean; shift?: boolean; alt?: boolean; meta?: boolean } = {}) {
    return computed(() => {
      const event = this._lastKeydown();
      if (event?.key !== key.toLowerCase()) return false;

      return (
        (modifiers.ctrl === undefined || event.ctrlKey === modifiers.ctrl) &&
        (modifiers.shift === undefined || event.shiftKey === modifiers.shift) &&
        (modifiers.alt === undefined || event.altKey === modifiers.alt) &&
        (modifiers.meta === undefined || event.metaKey === modifiers.meta)
      );
    });
  }

  onKeyCombo(
    key: string,
    modifiers: { ctrl?: boolean; shift?: boolean; alt?: boolean; meta?: boolean } = {},
    callback: (data: KeyboardEventData) => void,
  ) {
    const combo = this.createKeyCombo(key, modifiers);
    return effect(() => {
      if (combo()) {
        const data = this._lastKeydown();
        if (data) callback(data);
      }
    });
  }

  onCtrlA(callback: (data: KeyboardEventData) => void) {
    return effect(() => {
      if (this.isCtrlA()) {
        const data = this._lastKeydown();
        if (data) callback(data);
      }
    });
  }

  onCtrlC(callback: (data: KeyboardEventData) => void) {
    return effect(() => {
      if (this.isCtrlC()) {
        const data = this._lastKeydown();
        if (data) callback(data);
      }
    });
  }

  onCtrlV(callback: (data: KeyboardEventData) => void) {
    return effect(() => {
      if (this.isCtrlV()) {
        const data = this._lastKeydown();
        if (data) callback(data);
      }
    });
  }

  onCtrlX(callback: (data: KeyboardEventData) => void) {
    return effect(() => {
      if (this.isCtrlX()) {
        const data = this._lastKeydown();
        if (data) callback(data);
      }
    });
  }

  onCtrlS(callback: (data: KeyboardEventData) => void) {
    return effect(() => {
      if (this.isCtrlS()) {
        const data = this._lastKeydown();
        if (data) callback(data);
      }
    });
  }

  onCtrlZ(callback: (data: KeyboardEventData) => void) {
    return effect(() => {
      if (this.isCtrlZ()) {
        const data = this._lastKeydown();
        if (data) callback(data);
      }
    });
  }

  onEscape(callback: (data: KeyboardEventData) => void) {
    return effect(() => {
      if (this.isEscape()) {
        const data = this._lastKeydown();
        if (data) callback(data);
      }
    });
  }

  onEnter(callback: (data: KeyboardEventData) => void) {
    return effect(() => {
      if (this.isEnter()) {
        const data = this._lastKeydown();
        if (data) callback(data);
      }
    });
  }
}
